import dayjs from "dayjs";
import { filterExtraDocumentsOnly } from "./extraDocumentsFilter";

type RosterPrefillShift = {
  date: string;
  startTime?: string;
  endTime?: string;
  rate?: string | number;
  rateType?: "perHour" | "contracted";
  onCall?: boolean;
  onCallDetails?: string;
  specialty?: string;
  skillLevel?: string;
  description?: string;
};

export type RosterPrefill = {
  shifts: RosterPrefillShift[];
  postMode?: "group" | "separate";
  /** Calendar / multi-role rosters: each row is its own vacancy, not acceptCriteria alternates. */
  rosterSplitByEligibility?: boolean;
  specialty?: string;
  skillLevel?: string;
  supportLevel?: string;
  description?: string;
  travel?: string;
  accommodation?: string;
  additionalFilesRequired?: string[];
  documentUrls?: string[];
  specialtyAlternatives?: string[];
  skillLevelAlternatives?: string[];
  positionsRequired?: number;
};

const ROSTER_ACCEPTED_EXT = /\.(pdf|xlsx|xlsm|xls|csv|docx|txt|md)$/i;

export const isRosterUploadFile = (file: File) =>
  ROSTER_ACCEPTED_EXT.test(file.name || "");

export const normalizeRosterDate = (value: unknown): string => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const strict = dayjs(raw, ["YYYY-MM-DD", "YYYY/MM/DD", "DD/MM/YYYY", "DD-MM-YYYY", "DD.MM.YYYY"], true);
  if (strict.isValid()) return strict.format("YYYY/MM/DD");
  const fallback = dayjs(raw);
  if (fallback.isValid()) return fallback.format("YYYY/MM/DD");
  return "";
};

const MAX_AUTO_EXPAND_DAYS = 14;

const expandDateRange = (startRaw: unknown, endRaw?: unknown): string[] => {
  const start = normalizeRosterDate(startRaw);
  if (!start) return [];
  const end = normalizeRosterDate(endRaw) || start;
  const startDay = dayjs(start, "YYYY/MM/DD");
  const endDay = dayjs(end, "YYYY/MM/DD");
  if (!startDay.isValid() || !endDay.isValid()) return [start];
  if (endDay.isBefore(startDay)) return [start];

  const dates: string[] = [];
  let cursor = startDay;
  let guard = 0;
  while ((cursor.isBefore(endDay) || cursor.isSame(endDay, "day")) && guard < MAX_AUTO_EXPAND_DAYS) {
    dates.push(cursor.format("YYYY/MM/DD"));
    cursor = cursor.add(1, "day");
    guard += 1;
  }
  return dates;
};

const normalizeRateType = (value: unknown): "perHour" | "contracted" => {
  const raw = String(value || "").toLowerCase();
  if (raw.includes("hour")) return "perHour";
  return "contracted";
};

const isContractBlockRow = (row: any): boolean => {
  const rate = row?.rate_per_day ?? row?.ratePerDay;
  if (rate === null || rate === undefined || rate === "") return false;
  const n = Number(rate);
  return Number.isFinite(n) && n > 0;
};

const rowToShiftEntries = (row: any): RosterPrefillShift[] => {
  if (!row || typeof row !== "object") return [];

  const datesFromArray = Array.isArray(row.shift_dates)
    ? row.shift_dates.map(normalizeRosterDate).filter(Boolean)
    : [];
  let dates: string[] = [];
  if (isContractBlockRow(row)) {
    const start = normalizeRosterDate(row.start_date || row.date || datesFromArray[0]);
    if (!start) return [];
    dates = [start];
  } else if (datesFromArray.length > 0) {
    dates = datesFromArray;
  } else {
    const expanded = expandDateRange(row.start_date || row.date, row.end_date);
    if (expanded.length > MAX_AUTO_EXPAND_DAYS) {
      const start = normalizeRosterDate(row.start_date || row.date);
      dates = start ? [start] : [];
    } else {
      dates = expanded;
    }
  }

  const startTime = String(row.start_time || row.startTime || "").trim();
  const endTime = String(row.end_time || row.endTime || "").trim();
  const rateType = normalizeRateType(row.rate_type || row.rateType);
  const rate =
    rateType === "perHour"
      ? row.rate_per_hour ?? row.ratePerHour ?? row.rate ?? ""
      : row.rate_per_day ?? row.ratePerDay ?? row.rate ?? "";

  return dates.map((date: string) => ({
    date,
    startTime: startTime || undefined,
    endTime: endTime || undefined,
    rate: rate === null || rate === undefined ? "" : String(rate),
    rateType,
    onCall: Boolean(row.on_call ?? row.onCall),
    onCallDetails: String(row.on_call_details || row.onCallDetails || "").trim(),
    specialty: String(row.specialty || row.speciality || "").trim() || undefined,
    skillLevel: String(
      row.minimum_skill_level || row.skill_level || row.skillLevel || "",
    ).trim() || undefined,
    description: [
      String(row.description || row.position || "").trim(),
      isContractBlockRow(row) &&
      normalizeRosterDate(row.end_date) &&
      normalizeRosterDate(row.end_date) !== date
        ? `Until ${normalizeRosterDate(row.end_date)}`
        : "",
    ]
      .filter(Boolean)
      .join("\n") || undefined,
  }));
};

const dominantLabel = (values: string[]): string => {
  const counts: Record<string, number> = {};
  for (const v of values) {
    if (!v) continue;
    counts[v] = (counts[v] || 0) + 1;
  }
  let best = "";
  let bestCount = 0;
  for (const label of Object.keys(counts)) {
    const count = counts[label];
    if (count > bestCount) {
      best = label;
      bestCount = count;
    }
  }
  return best;
};

const explicitAlternatives = (
  first: any,
  extraction: any,
  field: "specialty" | "skill",
): string[] => {
  const keys =
    field === "specialty"
      ? ["specialty_alternatives"]
      : ["minimum_skill_level_alternatives", "skill_level_alternatives"];
  const out: string[] = [];
  for (const key of keys) {
    const fromFirst = first?.[key];
    const fromExtraction = extraction?.[key];
    if (Array.isArray(fromFirst)) out.push(...fromFirst);
    if (Array.isArray(fromExtraction)) out.push(...fromExtraction);
  }
  return Array.from(new Set(out.map((s) => String(s || "").trim()).filter(Boolean)));
};

const eligibilityBucketKey = (shift: RosterPrefillShift): string =>
  `${String(shift.specialty || "").trim().toLowerCase()}|${String(shift.skillLevel || "").trim().toLowerCase()}`;

export const mapRosterExtractionToPrefill = (
  extraction: any,
  documentUrl?: string,
): RosterPrefill | null => {
  if (!extraction || typeof extraction !== "object") return null;

  const rows: any[] = [];
  if (extraction.email_type === "multi_shift_listing" && Array.isArray(extraction.shifts)) {
    rows.push(...extraction.shifts);
  } else if (Array.isArray(extraction.shift_dates) && extraction.shift_dates.length > 0) {
    rows.push(extraction);
  } else if (extraction.start_date || extraction.date) {
    rows.push(extraction);
  } else if (Array.isArray(extraction.shifts)) {
    rows.push(...extraction.shifts);
  }

  const shifts = rows.flatMap(rowToShiftEntries).filter((s) => s.date);
  if (!shifts.length) return null;

  const first = rows[0] || extraction;
  const common = extraction.common_details || {};
  const isCalendarGrid = extraction._extraction_type === "calendar_grid";
  const eligibilityBuckets = new Set(
    shifts.map(eligibilityBucketKey).filter((k) => k !== "|"),
  );
  const rosterSplitByEligibility =
    isCalendarGrid || eligibilityBuckets.size > 1;

  const sortedShifts = [...shifts].sort((a, b) => a.date.localeCompare(b.date));
  const anchorShift = sortedShifts[0];

  const primarySpecialty = rosterSplitByEligibility
    ? String(anchorShift?.specialty || "").trim()
    : dominantLabel(shifts.map((s) => s.specialty || "")) ||
      String(
        first.specialty ||
          first.speciality ||
          extraction.specialty ||
          "",
      ).trim();
  const primarySkill = rosterSplitByEligibility
    ? String(anchorShift?.skillLevel || "").trim()
    : dominantLabel(shifts.map((s) => s.skillLevel || "")) ||
      String(
        first.minimum_skill_level ||
          first.skill_level ||
          extraction.minimum_skill_level ||
          "",
      ).trim();

  // "Also open to" only from explicit LLM/email fields — never from other roster rows.
  const specialtyAlternatives = rosterSplitByEligibility
    ? []
    : explicitAlternatives(first, extraction, "specialty").filter(
        (s) => s !== primarySpecialty,
      );
  const skillLevelAlternatives = rosterSplitByEligibility
    ? []
    : explicitAlternatives(first, extraction, "skill").filter(
        (s) => s !== primarySkill,
      );

  const travel =
    String(
      extraction.travel ||
        extraction.travel_allowance ||
        common.travel ||
        common.travel_allowance ||
        common.locum_allowances ||
        first.travel ||
        first.travel_allowance ||
        "",
    ).trim() || undefined;
  const accommodation =
    String(
      extraction.accommodation ||
        extraction.accommodation_allowance ||
        common.accommodation ||
        common.accommodation_allowance ||
        first.accommodation ||
        first.accommodation_allowance ||
        "",
    ).trim() || undefined;

  const additionalFilesRequired = filterExtraDocumentsOnly(
    Array.isArray(extraction.documents_required)
      ? extraction.documents_required
      : Array.isArray(common.documents_required)
        ? common.documents_required
        : [],
  );

  return {
    shifts,
    postMode: rosterSplitByEligibility
      ? "separate"
      : shifts.length > 1
        ? "group"
        : "separate",
    rosterSplitByEligibility,
    specialty: primarySpecialty,
    skillLevel: primarySkill,
    supportLevel: String(first.support_level || first.supportLevel || "").trim(),
    description: [
      first.description || first.position || extraction.description || "",
      ...(Array.isArray(common.placement_notes) ? common.placement_notes : []),
      common.extra_documents_notes || "",
    ]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join("\n"),
    travel,
    accommodation,
    additionalFilesRequired: additionalFilesRequired.length
      ? additionalFilesRequired
      : undefined,
    documentUrls: documentUrl ? [documentUrl] : undefined,
    specialtyAlternatives: specialtyAlternatives.length
      ? specialtyAlternatives
      : undefined,
    skillLevelAlternatives: skillLevelAlternatives.length
      ? skillLevelAlternatives
      : undefined,
    positionsRequired:
      typeof first.positions_required === "number"
        ? first.positions_required
        : typeof extraction.positions_required === "number"
          ? extraction.positions_required
          : undefined,
  };
};

export const mapRosterUploadResponseToPrefill = (
  response: any,
): RosterPrefill | null => {
  const parseResult = response?.parseResult;
  const extraction =
    parseResult?.extraction ||
    (parseResult?.status === "success" ? parseResult : null);
  if (!extraction) return null;
  const inner =
    extraction.extraction && typeof extraction.extraction === "object"
      ? extraction.extraction
      : extraction;
  return mapRosterExtractionToPrefill(inner, response?.documentUrl);
};
